/**
 * Funkcje pomocnicze do obsługi rozstawu osi i wymiarów pojazdu w dokumencie
 * Certificate of Conformity. Udostępniają czyste funkcje do wyodrębniania tych
 * danych z opcji zamówienia oraz do budowania wartości patchy formularza.
 *
 * @module wheelbase-utils
 * @since 11.08.2026
 */

import { buildProducerConversionKey } from "../../certificate-of-conformity-variables.constants";
import { VehicleLine, resolveVehicleLine } from "../vehicle-line";
import {
  findSelectedOptionByParentId,
  type OrderOption,
} from "../order-options";
import { IVECO_WHEELBASES } from "./iveco-wheelbases";
import {
  getMercedesHighDeckWheelbase,
  getMercedesHighDeckWheelbaseOptions,
  getMercedesHighDeckWheelbaseReferenceTable,
} from "./mercedes-tourist-line-highdeck-wheelbase";

/** Identyfikator nadrzędny (parentId) dla opcji rozstawu osi w tablicy order.options */
export const WHEELBASE_PARENT_ID = "60acddefab5b200dd5eddd02";

/**
 * Identyfikator nadrzędny (parentId) dla opcji z wprost podanymi wymiarami pojazdu
 * w tablicy order.options - nazwa opcji zawiera już gotowe wartości
 * (np. "długość: 7986 mm, szerokość: 2250 mm, wysokość: 3120 mm, zwis tylny: 2640 mm").
 */
export const VEHICLE_DIMENSIONS_PARENT_ID = "60b60e3b2fbe0fff4935bb0e";

/**
 * Wzorzec regex do wyodrębnienia wartości rozstawu osi w mm z nazwy opcji.
 * Nazwa opcji zawiera dokładnie jedną taką wartość, ewentualnie z dowolnym
 * tekstem po niej (np. "Rozstaw osi 4750mm" albo
 * "Rozstaw osi 4325 mm - High Deck (drzwi kierowcy)") - dlatego dopasowujemy
 * pierwszą liczbę zakończoną "mm", ignorując resztę tekstu.
 */
const WHEELBASE_MM_VALUE_PATTERN = /(\d+)\s*mm/i;

/**
 * Domyślny fizyczny rozstaw osi [mm] dla zwykłego Mercedes Benz Tourist Line
 * (Sprinter) - ta zabudowa ma jeden wariant podwozia, więc gdy zamówienie nie
 * niesie opcji rozstawu osi, przyjmujemy tę stałą wartość jako zabezpieczenie.
 */
const MERCEDES_TOURIST_LINE_DEFAULT_WHEELBASE_MM = "4325";

/**
 * Mapowanie etykiet występujących w nazwie opcji z wymiarami pojazdu na nazwy pól
 * formularza.
 */
const VEHICLE_DIMENSION_FIELD_LABELS: Record<string, string> = {
  length: "długość",
  width: "szerokość",
  height: "wysokość",
  rearOverhang: "zwis tylny",
};

/** Parametry potrzebne do wyznaczenia wartości formularza dla rozstawu osi */
export interface ResolveWheelbaseParams {
  orderOptions: OrderOption[] | undefined | null;
  producentName: string | undefined;
  conversionName: string | undefined;
  /** Kod tonażu ({{versionTonnage}}) - używany tylko dla zabudów wyznaczających versionWheelbase z liczby pasażerów (patrz Mercedes HighDeck) */
  tonnage?: string | undefined | null;
  /** Całkowita liczba pasażerów (order.places.variants[0].passengersCount) - jw. */
  passengersCount?: number | string | undefined | null;
  /** Liczba pilotów (order.places.variants[0].pilotsCount) - jw., rozstrzyga część niejednoznacznych kombinacji dla Mercedes HighDeck */
  pilotsCount?: number | string | undefined | null;
}

/**
 * Wyodrębnia wartość rozstawu osi z nazwy opcji za pomocą regex.
 *
 * @param optionName - Nazwa opcji (np. "Rozstaw osi 4750mm" albo
 *   "Rozstaw osi 4325 mm - High Deck (drzwi kierowcy)")
 * @returns Wyodrębniona wartość rozstawu osi (np. "4750") lub null, jeśli nie znaleziono
 *
 * @example
 * ```typescript
 * extractWheelbaseFromName("Rozstaw osi 4750mm") // "4750"
 * extractWheelbaseFromName("Rozstaw osi 4325 mm - High Deck (drzwi kierowcy)") // "4325"
 * extractWheelbaseFromName("Invalid name") // null
 * ```
 */
export function extractWheelbaseFromName(
  optionName: string | undefined,
): string | null {
  if (!optionName) return null;
  return optionName.match(WHEELBASE_MM_VALUE_PATTERN)?.[1] ?? null;
}

/**
 * Wyodrębnia rozstaw osi z opcji zamówienia. Łączy znalezienie opcji
 * (WHEELBASE_PARENT_ID) i wyodrębnienie wartości.
 *
 * @param options - Tablica opcji zamówienia
 * @returns Wartość rozstawu osi (np. "4750") lub null
 */
export function extractWheelbaseFromOrderOptions(
  options: OrderOption[] | undefined | null,
): string | null {
  const wheelbaseOption = findSelectedOptionByParentId(
    options,
    WHEELBASE_PARENT_ID,
  );
  return extractWheelbaseFromName(wheelbaseOption?.name);
}

/**
 * Wyodrębnia wartość liczbową w mm występującą po podanej etykiecie w tekście
 * (np. dla etykiety "długość" i tekstu "długość: 7986 mm" zwraca "7986").
 * Dopasowanie jest niezależne od tego, co poprzedza etykietę (spacja, przecinek).
 */
function extractLabeledMmValue(text: string, label: string): string | null {
  const pattern = new RegExp(`${label}\\s*:\\s*(\\d+)\\s*mm`, "i");
  return text.match(pattern)?.[1] ?? null;
}

/**
 * Wyodrębnia wymiary pojazdu (długość, szerokość, wysokość, zwis tylny) z nazwy
 * opcji zamówienia, np.:
 * "długość: 7986 mm, szerokość: 2250 mm, wysokość: 3120 mm, zwis tylny: 2640 mm".
 *
 * @param optionName - Nazwa opcji z wymiarami pojazdu
 * @returns Wartości pól formularza (length/width/height/rearOverhang) lub null,
 *   jeśli nie znaleziono żadnej z wartości
 *
 * @example
 * ```typescript
 * extractVehicleDimensionsFromName(
 *   "długość: 7986 mm, szerokość: 2250 mm, wysokość: 3120 mm, zwis tylny: 2640 mm"
 * )
 * // { length: "7986", width: "2250", height: "3120", rearOverhang: "2640" }
 * ```
 */
export function extractVehicleDimensionsFromName(
  optionName: string | undefined,
): Record<string, string> | null {
  if (!optionName) return null;
  const dimensions: Record<string, string> = {};
  for (const [field, label] of Object.entries(VEHICLE_DIMENSION_FIELD_LABELS)) {
    const value = extractLabeledMmValue(optionName, label);
    if (value) dimensions[field] = value;
  }
  return Object.keys(dimensions).length > 0 ? dimensions : null;
}

/**
 * Wyodrębnia wymiary pojazdu z zaznaczonej opcji zamówienia
 * (parentId = VEHICLE_DIMENSIONS_PARENT_ID). Łączy znalezienie opcji
 * i wyodrębnienie wartości.
 *
 * @param options - Tablica opcji zamówienia
 * @returns Wartości pól formularza (length/width/height/rearOverhang) lub null
 */
export function resolveVehicleDimensionsFormValues(
  options: OrderOption[] | undefined | null,
): Record<string, string> | null {
  const dimensionsOption = findSelectedOptionByParentId(
    options,
    VEHICLE_DIMENSIONS_PARENT_ID,
  );
  return extractVehicleDimensionsFromName(dimensionsOption?.name);
}

/**
 * Pobiera kod wersji rozstawu osi (np. "W01") dla Iveco Tourist Line, na
 * podstawie fizycznego rozstawu osi i długości pojazdu (patrz
 * iveco-wheelbases.ts).
 *
 * @param wheelbaseValue - Wartość rozstawu osi (np. "4750")
 * @param lengthValue - Wartość długości (np. "8398")
 * @returns Kod wersji rozstawu osi lub null, jeśli nie znaleziono
 */
export function getVersionWheelbase(
  wheelbaseValue: string,
  lengthValue: string,
): string | null {
  return IVECO_WHEELBASES[wheelbaseValue]?.[lengthValue] ?? null;
}

/**
 * Zwraca opcje rozstawu osi ({{versionWheelbase}}) do wyboru w formularzu dla
 * podanej zabudowy - źródłem jest zawsze faktycznie zadeklarowana dla tej
 * zabudowy tabela danych (IVECO_WHEELBASES / PASSENGERS_TO_WHEELBASE), a nie
 * dowolnie przyjęty zakres numeryczny (poprzednio: W01-W08 / W05-W14 "z
 * palca") - dzięki temu lista pokazuje dokładnie te warianty, dla których
 * mamy realne dane, i rośnie automatycznie w miarę uzupełniania tych tabel.
 *
 * Dla Mercedes HighDeck tonaż, liczba pasażerów i obecność pilotki zawężają
 * listę do wariantów W pasujących do wszystkich znanych faktów (np. tylko
 * W12 dla tonażu "68", 24 pasażerów i pilotki na pokładzie, zamiast W12+W13)
 * - te fakty są już znane w chwili renderowania formularza (z danych
 * zamówienia). Gdy któryś z nich nie zawęża niczego, lista wraca do
 * szerszego zestawu.
 *
 * @param line - Zabudowa (VehicleLine) albo null, gdy nierozpoznana
 * @param tonnage - Kod tonażu ({{versionTonnage}}) - używany tylko dla Mercedes HighDeck
 * @param passengersCount - Całkowita liczba pasażerów (order.places.variants[0].passengersCount) - jw.
 * @param pilotsCount - Liczba pilotów (order.places.variants[0].pilotsCount) - jw.
 * @returns Lista opcji (może być pusta, gdy zabudowa/tonaż nie mają zarejestrowanej tabeli)
 */
export function getWheelbaseSelectOptions(
  line: VehicleLine | null,
  tonnage?: string | undefined | null,
  passengersCount?: number | string | undefined | null,
  pilotsCount?: number | string | undefined | null,
): Array<{ value: string; label: string }> {
  if (line === VehicleLine.IvecoTouristLine) {
    const codes = new Set<string>();
    for (const byLength of Object.values(IVECO_WHEELBASES)) {
      Object.values(byLength).forEach((code) => codes.add(code));
    }
    return [...codes]
      .sort((a, b) => parseInt(a.slice(1), 10) - parseInt(b.slice(1), 10))
      .map((code) => ({ value: code, label: code }));
  }
  if (line === VehicleLine.MercedesTouristLineHighDeck) {
    return getMercedesHighDeckWheelbaseOptions(
      tonnage,
      passengersCount,
      pilotsCount,
    );
  }
  return [];
}

/**
 * Zwraca tabelkę informacyjną (kod W / opis, np. "24 miejsca (z lub bez
 * pilotki)") dla podanej zabudowy i tonażu - do pokazania użytkownikowi w
 * formularzu, gdy resolveWheelbaseFormValues nie ustawiło automatycznie
 * {{versionWheelbase}} (niejednoznaczna liczba pasażerów albo brak
 * dopasowania - patrz mercedes-tourist-line-highdeck-wheelbase.ts), żeby
 * dało się wybrać właściwy wariant ręcznie ze świadomością, co on oznacza.
 * Dla zabudów bez takiej tabeli (dziś: Iveco - kod W nie jest tam
 * niejednoznaczny) zwraca pustą listę.
 *
 * @param line - Zabudowa (VehicleLine) albo null, gdy nierozpoznana
 * @param tonnage - Kod tonażu ({{versionTonnage}}) - używany tylko dla Mercedes HighDeck
 */
export function getWheelbaseReferenceTable(
  line: VehicleLine | null,
  tonnage?: string | undefined | null,
): Array<{ code: string; description: string }> {
  if (line === VehicleLine.MercedesTouristLineHighDeck) {
    return getMercedesHighDeckWheelbaseReferenceTable(tonnage);
  }
  return [];
}

/**
 * Zarządza wyodrębnianiem wartości rozstawu osi i wymiarów pojazdu oraz
 * budowaniem patcha formularza. Główny punkt wejścia - łączy wszystkie kroki
 * w jedną operację.
 *
 * Wymiary fizyczne (length/width/height/rearOverhang) pochodzą zawsze wprost
 * z opcji VEHICLE_DIMENSIONS_PARENT_ID, jeśli jest zaznaczona. Rozstaw osi
 * (wheelbase) i wynikające z niego axleSpacing (zawsze równe wheelbase)
 * pochodzą z opcji WHEELBASE_PARENT_ID - niezależnie od zabudowy, to zawsze
 * fizyczne wymiary pojazdu. Wyjątek: zwykły Mercedes Benz Tourist Line, gdy
 * zamówienie nie niesie opcji rozstawu osi - przyjmowana jest wtedy stała
 * MERCEDES_TOURIST_LINE_DEFAULT_WHEELBASE_MM ("4325").
 *
 * Kod wersji (versionWheelbase) wyznaczany jest inaczej w zależności od
 * zabudowy:
 * - Mercedes Benz Tourist Line HighDeck: z tonażu + całkowitej liczby
 *   pasażerów (patrz mercedes-tourist-line-highdeck-wheelbase.ts) - fizyczny
 *   rozstaw osi/długość NIE są tu użyte (nie rozróżniają wariantów W tak jak
 *   w Iveco),
 * - Iveco Tourist Line: z rozstawu osi ORAZ długości (z wymiarów pojazdu) -
 *   żeby jednoznacznie wskazać wariant w tabeli IVECO_WHEELBASES (ten sam
 *   rozstaw może mieć kilka wariantów o różnej długości),
 * - pozostałe zabudowy: brak zarejestrowanej logiki - versionWheelbase nie
 *   jest ustawiane automatycznie.
 *
 * @param params - Obiekt zawierający wszystkie niezbędne zależności
 * @returns Wartości patcha formularza lub null, jeśli nic nie udało się wyznaczyć
 *
 * @example
 * ```typescript
 * const patchValues = resolveWheelbaseFormValues({
 *   orderOptions: order.options,
 *   producentName: order.producent?.name,
 *   conversionName: order.conversion?.name,
 *   tonnage: versionTonnage,
 *   passengersCount: order.places?.variants?.[0]?.passengersCount,
 *   pilotsCount: order.places?.variants?.[0]?.pilotsCount,
 * });
 *
 * if (patchValues) {
 *   form.patchValue(patchValues, { emitEvent: false });
 * }
 * ```
 */
export function resolveWheelbaseFormValues(
  params: ResolveWheelbaseParams,
): Record<string, string> | null {
  const {
    orderOptions,
    producentName,
    conversionName,
    tonnage,
    passengersCount,
    pilotsCount,
  } = params;

  // Wymiary fizyczne pojazdu - zawsze wprost z opcji wymiarów, jeśli zaznaczona
  const vehicleDimensions = resolveVehicleDimensionsFormValues(orderOptions);

  // Rozstaw osi - z osobnej opcji rozstawu osi
  const wheelbaseValue = extractWheelbaseFromOrderOptions(orderOptions);

  const patch: Record<string, string> = { ...vehicleDimensions };

  if (wheelbaseValue) {
    patch.wheelbase = wheelbaseValue;
    // axleSpacing jest zawsze równe wheelbase - nie wymaga osobnego wpisu w tabeli
    patch.axleSpacing = wheelbaseValue;
  }

  const producerKey = buildProducerConversionKey(producentName, conversionName);
  const line = resolveVehicleLine(producerKey);

  // Zwykły Mercedes Benz Tourist Line: gdy zamówienie nie ma opcji rozstawu
  // osi, przyjmujemy stały rozstaw tej zabudowy (jeden wariant podwozia).
  if (line === VehicleLine.MercedesTouristLine && !wheelbaseValue) {
    patch.wheelbase = MERCEDES_TOURIST_LINE_DEFAULT_WHEELBASE_MM;
    // axleSpacing jest zawsze równe wheelbase
    patch.axleSpacing = MERCEDES_TOURIST_LINE_DEFAULT_WHEELBASE_MM;
  }

  if (line === VehicleLine.MercedesTouristLineHighDeck) {
    const versionWheelbase = getMercedesHighDeckWheelbase(
      tonnage,
      passengersCount,
      pilotsCount,
    );
    if (versionWheelbase) {
      patch.versionWheelbase = versionWheelbase;
    } else {
      console.warn(
        `[wheelbase-utils] Nie znaleziono jednoznacznego wariantu (versionWheelbase) dla "${producerKey}" / tonaż "${tonnage}" / liczba pasażerów "${passengersCount}" / pilotów "${pilotsCount}" - patrz mercedes-tourist-line-highdeck-wheelbase.ts (możliwa niejednoznaczność) - uzupełnij ręcznie.`,
      );
    }
  } else if (line === VehicleLine.IvecoTouristLine && wheelbaseValue) {
    // Kod wersji wymaga też długości, żeby rozróżnić warianty o tym samym
    // rozstawie (np. Iveco 4750mm: W01 dla długości 8398mm, W04 dla 7990mm)
    const lengthValue = vehicleDimensions?.length;
    if (lengthValue) {
      const versionWheelbase = getVersionWheelbase(wheelbaseValue, lengthValue);
      if (versionWheelbase) {
        patch.versionWheelbase = versionWheelbase;
      } else {
        console.warn(
          `[wheelbase-utils] Nie znaleziono wariantu (versionWheelbase) dla "${producerKey}" / rozstaw "${wheelbaseValue}" / długość "${lengthValue}"`,
        );
      }
    }
  }

  return Object.keys(patch).length > 0 ? patch : null;
}
